import React, { memo, useMemo, useState } from 'react';
import greetingGif from '../../assets/temi/gif/temi-greeting.gif';
import idleGif from '../../assets/temi/gif/temi-idle.gif';
import listeningGif from '../../assets/temi/gif/temi-listening.gif';
import speakingGif from '../../assets/temi/gif/temi-speaking.gif';
import successGif from '../../assets/temi/gif/temi-success.gif';
import surprisedGif from '../../assets/temi/gif/temi-surprised.gif';
import thinkingGif from '../../assets/temi/gif/temi-thinking.gif';
import './avatarAnimations.scss';

type AvatarSize = 'small' | 'medium' | 'large';
type AvatarVisualState = 'greeting' | 'speaking' | 'idle' | 'listening' | 'thinking' | 'surprised' | 'success';

type AvatarState = {
  phase?: string;
  aiIntent?: string;
  emotion?: string;
  speaking?: boolean;
  listening?: boolean;
  thinking?: boolean;
  error?: boolean;
  warning?: boolean;
  celebrating?: boolean;
  disconnected?: boolean;
  loading?: boolean;
};

type AvatarControllerProps = {
  avatarState?: AvatarState;
  emotion?: string;
  isSpeaking?: boolean;
  size?: AvatarSize;
  className?: string;
};

const sizeClassMap: Record<AvatarSize, string> = {
  small: 'avatar-controller--small',
  medium: 'avatar-controller--medium',
  large: 'avatar-controller--large',
};

const visualGifMap: Record<AvatarVisualState, string> = {
  greeting: greetingGif,
  speaking: speakingGif,
  idle: idleGif,
  listening: listeningGif,
  thinking: thinkingGif,
  surprised: surprisedGif,
  success: successGif,
};

const truthy = (value: unknown) => value === true;

const resolveVisualState = (state: AvatarState): AvatarVisualState => {
  const phase = state.phase || 'idle';
  const aiIntent = state.aiIntent || '';
  const emotion = state.emotion || '';

  if (phase === 'greeting') return 'greeting';
  if (state.error || state.warning || state.disconnected || emotion === 'surprised' || aiIntent === 'warning' || aiIntent === 'low_quality') {
    return 'surprised';
  }
  if (state.celebrating || phase === 'done' || phase === 'success' || aiIntent === 'celebrating') {
    return 'success';
  }
  if (state.speaking || phase === 'ai_speaking' || aiIntent === 'explaining') {
    return 'speaking';
  }
  if (state.thinking || state.loading || phase === 'review' || phase === 'submitting' || phase === 'finishing' || aiIntent === 'processing' || aiIntent === 'reviewing' || aiIntent === 'thinking') {
    return 'thinking';
  }
  if (state.listening || phase === 'recording') {
    return 'listening';
  }

  return 'idle';
};

const getStateLabel = (visualState: AvatarVisualState) => {
  if (visualState === 'surprised') return 'warning';
  return visualState;
};

const AvatarController = ({
  avatarState,
  emotion,
  isSpeaking,
  size = 'medium',
  className = '',
}: AvatarControllerProps) => {
  const [assetFailed, setAssetFailed] = useState(false);

  const normalizedState = useMemo<AvatarState>(() => ({
    ...(avatarState || {}),
    emotion: avatarState?.emotion || emotion,
    speaking: avatarState?.speaking ?? truthy(isSpeaking),
  }), [avatarState, emotion, isSpeaking]);

  const visualState = useMemo(() => resolveVisualState(normalizedState), [normalizedState]);
  const currentGif = assetFailed ? idleGif : visualGifMap[visualState] || idleGif;
  const stateLabel = getStateLabel(visualState);

  const classes = [
    'avatar-controller',
    sizeClassMap[size] || sizeClassMap.medium,
    `avatar-controller--state-${stateLabel}`,
    `avatar-controller--tone-${stateLabel}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      data-avatar-state={visualState}
      data-ai-intent={normalizedState.aiIntent || 'none'}
      aria-hidden="true"
    >
      <div className="avatar-controller__halo" />
      <div className="avatar-controller__sprite-wrap">
        <img
          key={currentGif}
          className="avatar-controller__sprite"
          src={currentGif}
          alt=""
          draggable="false"
          onError={() => setAssetFailed(true)}
        />
      </div>
      <div className="avatar-controller__shadow" />
    </div>
  );
};

export default memo(AvatarController);
